import { useTranslation } from "react-i18next";

import {
  ICustomFieldAnswer,
  ICustomFieldFormItem,
} from "../../../../@types/CustomField";
import { IOption, SelectDropdown } from "../../../../components/SelectDropdown";
import { TextField } from "../../../../components/TextField";
import { Toggle } from "../../../../components/Toggle";
import {
  CustomFieldAnswers,
  CustomFieldErrors,
} from "../../../../utils/customFields";
import {
  RequiredHint,
  SectionHeader,
  SectionTitle,
  ToggleContainer,
} from "./AdditionalDetailsSection.styles";
import {
  dropdownCss,
  dropdownPopUpCss,
  ErrorMessage,
  FormField,
  inputCss,
  SectionContainer,
} from "./AddPatientModal.styles";
import { CustomFieldDateInput } from "./CustomFieldDateInput";

type AdditionalDetailsSectionProps = {
  items: ICustomFieldFormItem[];
  answers: CustomFieldAnswers;
  errors: CustomFieldErrors;
  // Ids of fields that flipped from optional to required after a ward was added this session.
  newlyRequiredIds: string[];
  // Portal country, so Date fields follow the same segment order as the Date of birth field.
  countryCode?: string;
  onChange: (definitionId: string, answer: ICustomFieldAnswer) => void;
};

// Turns a field's option strings into SelectDropdown options (value === label for custom fields).
function toOptions(options: string[]): IOption[] {
  return options.map((option) => ({
    label: option,
    value: option,
    key: option,
  }));
}

// EN-5067: renders the dynamic "Additional details" section on Add Patient. The field set comes
// already merged and ordered (by displayOrder) from the API — we render it as-is, never merging or
// grouping per ward.
export function AdditionalDetailsSection({
  items,
  answers,
  errors,
  newlyRequiredIds,
  countryCode,
  onChange,
}: AdditionalDetailsSectionProps) {
  const { t } = useTranslation();

  if (items.length === 0) return null;

  const renderWidget = (item: ICustomFieldFormItem) => {
    const id = item.customFieldDefinitionId;
    const answer = answers[id];
    const inputName = `custom-field-${id}`;

    switch (item.type) {
      case "YesNo":
        return (
          <ToggleContainer>
            <Toggle
              checked={answer?.value === "true"}
              onCheckedChange={(checked: boolean) =>
                onChange(id, { value: checked ? "true" : "false" })
              }
            />
          </ToggleContainer>
        );

      case "Date":
        return (
          <CustomFieldDateInput
            name={inputName}
            value={answer?.value}
            isRequired={item.isRequired}
            errorMessage={errors[id]}
            countryCode={countryCode}
            onChange={(value) => onChange(id, { value })}
          />
        );

      case "Integer":
      case "Decimal":
        return (
          <TextField
            label=""
            name={inputName}
            type="number"
            placeholder={item.label}
            value={answer?.value ?? ""}
            onChange={(event) => onChange(id, { value: event.target.value })}
            onKeyDown={(evt) =>
              ["e", "E", "+"].includes(evt.key) && evt.preventDefault()
            }
            backgroudColor="white"
            className={inputCss()}
            errorMessage={errors[id]}
          />
        );

      case "Dropdown":
        return (
          <>
            <SelectDropdown
              popUpCsss={dropdownPopUpCss()}
              className={dropdownCss()}
              options={toOptions(item.options)}
              width={410}
              placeholder={t("addPatient_placeholders_chooseField", {
                field: item.label.toLowerCase(),
              })}
              value={answer?.value}
              onValueChange={(value: string | string[]) =>
                onChange(id, { value: value.toString() })
              }
            />
            <ErrorMessage>{errors[id]}</ErrorMessage>
          </>
        );

      case "Multiselect":
        return (
          <>
            <SelectDropdown
              multiple
              popUpCsss={dropdownPopUpCss()}
              className={dropdownCss()}
              options={toOptions(item.options)}
              width={410}
              placeholder={t("addPatient_placeholders_chooseField", {
                field: item.label.toLowerCase(),
              })}
              value={answer?.selectedOptions ?? []}
              onValueChange={(value: string | string[]) =>
                onChange(id, {
                  selectedOptions: Array.isArray(value) ? value : [value],
                })
              }
            />
            <ErrorMessage>{errors[id]}</ErrorMessage>
          </>
        );

      case "FreeText":
      default:
        return (
          <TextField
            label=""
            name={inputName}
            placeholder={item.label}
            value={answer?.value ?? ""}
            onChange={(event) => onChange(id, { value: event.target.value })}
            backgroudColor="white"
            className={inputCss()}
            errorMessage={errors[id]}
          />
        );
    }
  };

  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>{t("patientList_additionalDetails")}</SectionTitle>
      </SectionHeader>

      {items.map((item) => {
        const id = item.customFieldDefinitionId;
        return (
          <FormField key={id}>
            <p>
              {item.label}
              {item.isRequired ? " *" : ""}
              {newlyRequiredIds.includes(id) && (
                <RequiredHint>{t("addPatient_nowRequired")}</RequiredHint>
              )}
            </p>
            {renderWidget(item)}
          </FormField>
        );
      })}
    </SectionContainer>
  );
}
